/* ==========================================================================
   Flash sections — tiny helpers (no external lib; Swiper is already loaded
   by the theme). Handles:
   - evergreen countdown          [data-flash-countdown]
   - gift box "orders left"       [data-flash-gift]
   - UGC video carousel + mute    <flash-ugc-videos>
   - reviews slider / marquee     <flash-reviews>
   ========================================================================== */
(function () {
  'use strict';
  if (window.__flashSectionsLoaded) return; // the script can be included by several sections
  window.__flashSectionsLoaded = true;

  /* ---------------------------------------------------------------- utils */
  function pad(n) {
    return String(n).padStart(2, '0');
  }
  function storage() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }
  function whenSwiper(cb, tries) {
    if (window.Swiper) return cb();
    tries = tries || 0;
    if (tries > 50) return;
    setTimeout(function () {
      whenSwiper(cb, tries + 1);
    }, 100);
  }

  /* ------------------------------------------------- evergreen countdown */
  function initCountdown(el) {
    if (el.dataset.flashInit) return;
    el.dataset.flashInit = '1';
    var timer = el.querySelector('[data-flash-timer]');
    if (!timer) return;
    var mode = el.dataset.mode || 'daily';
    var duration = parseInt(el.dataset.duration, 10) || 0; // minutes
    var key = el.dataset.key || 'flash-countdown';
    var store = storage();

    function getEnd() {
      var now = new Date();
      if (mode === 'session' && duration > 0) {
        var end = store ? parseInt(store.getItem(key), 10) : 0;
        if (!end || end <= now.getTime()) {
          end = now.getTime() + duration * 60 * 1000;
          if (store) store.setItem(key, String(end));
        }
        return end;
      }
      // daily: next local midnight
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0).getTime();
    }

    var end = getEnd();
    function tick() {
      var diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (diff === 0) end = getEnd();
      var h = Math.floor(diff / 3600);
      var m = Math.floor((diff % 3600) / 60);
      var s = diff % 60;
      timer.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
    }
    tick();
    setInterval(tick, 1000);
  }

  /* --------------------------------------------------- gift orders count */
  function initGift(el) {
    if (el.dataset.flashInit) return;
    el.dataset.flashInit = '1';
    var target = el.querySelector('[data-flash-orders-count]');
    if (!target) return;
    var start = parseInt(el.dataset.count, 10) || 25;
    var min = parseInt(el.dataset.min, 10) || 1;
    var animate = el.dataset.animate === 'true';
    var count = start;
    target.textContent = count;
    if (!animate || min >= start) return;
    function step() {
      if (count > min) count--;
      target.textContent = count;
      var delay = Math.floor(Math.random() * (20000 - 4000 + 1)) + 4000;
      setTimeout(step, delay);
    }
    setTimeout(step, Math.floor(Math.random() * 8000) + 4000);
  }

  /* --------------------------------------------------- UGC video carousel */
  function initUgc(el) {
    if (el.dataset.flashInit) return;
    el.dataset.flashInit = '1';
    var swiperEl = el.querySelector('.flash-ugc__swiper');
    if (!swiperEl) return;
    var slidesMobile = parseFloat(el.dataset.mobile) || 1.7;
    var slidesTablet = parseFloat(el.dataset.tablet) || 2;
    var slidesDesktop = parseFloat(el.dataset.desktop) || 4;
    var gapMobile = parseInt(el.dataset.gapMobile, 10) || 0;
    var gap = parseInt(el.dataset.gap, 10) || 26;
    var loop = el.dataset.loop !== 'false';
    var current = el.querySelector('[data-flash-current]');
    var videos = el.querySelectorAll('video');

    function playVisible(swiper) {
      swiper.slides.forEach(function (slide, i) {
        var v = slide.querySelector('video');
        if (!v) return;
        var visible = slide.classList.contains('swiper-slide-visible') || i === swiper.activeIndex;
        if (visible) {
          if (v.dataset.src && !v.getAttribute('src')) {
            v.setAttribute('src', v.dataset.src);
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }

    whenSwiper(function () {
      swiperEl.classList.remove('flash-ugc__swiper--preview');
      var swiper = new Swiper(swiperEl, {
        speed: 300,
        loop: loop,
        watchSlidesProgress: true,
        navigation: {
          nextEl: el.querySelector('.flash-ugc__next'),
          prevEl: el.querySelector('.flash-ugc__prev'),
        },
        breakpoints: {
          320: { centeredSlides: true, spaceBetween: gapMobile, slidesPerView: slidesMobile },
          750: { centeredSlides: false, spaceBetween: gapMobile, slidesPerView: slidesTablet },
          990: { centeredSlides: false, spaceBetween: gap, slidesPerView: slidesDesktop },
        },
        on: {
          init: function () {
            playVisible(this);
          },
          slideChangeTransitionEnd: function () {
            if (current) current.textContent = this.realIndex + 1;
            playVisible(this);
          },
        },
      });
      // lazy start when the section enters the viewport
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              playVisible(swiper);
            } else {
              videos.forEach(function (v) {
                v.pause();
              });
            }
          });
        }, { rootMargin: '100px 0px' });
        io.observe(el);
      }
    });

    // mute / unmute buttons
    el.querySelectorAll('.flash-ugc__mute').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.closest('.flash-ugc__media').querySelector('video');
        if (!v) return;
        var muted = !v.muted;
        // only one unmuted video at a time
        videos.forEach(function (o) {
          o.muted = true;
          o.closest('.flash-ugc__media').classList.remove('is-unmuted');
        });
        v.muted = muted;
        btn.closest('.flash-ugc__media').classList.toggle('is-unmuted', !muted);
        btn.setAttribute('aria-pressed', String(!muted));
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    });
  }

  /* ------------------------------------------------------ reviews slider */
  function initReviews(el) {
    if (el.dataset.flashInit) return;
    el.dataset.flashInit = '1';
    if (el.dataset.mode === 'marquee') return; // pure CSS
    var swiperEl = el.querySelector('.flash-reviews__swiper');
    if (!swiperEl) return;
    var slidesMobile = parseFloat(el.dataset.mobile) || 1.3;
    var slidesDesktop = parseFloat(el.dataset.desktop) || 4;
    var slidesTablet = Math.min(slidesDesktop, 2.5);
    var gapMobile = parseInt(el.dataset.gapMobile, 10) || 16;
    var gap = parseInt(el.dataset.gap, 10) || 20;
    var autoplay = el.dataset.autoplay === 'true' ? { delay: (parseInt(el.dataset.autoplaySpeed, 10) || 4) * 1000, disableOnInteraction: false } : false;
    whenSwiper(function () {
      swiperEl.classList.remove('flash-reviews__swiper--preview');
      new Swiper(swiperEl, {
        speed: 400,
        loop: true,
        autoplay: autoplay,
        navigation: {
          nextEl: el.querySelector('.flash-reviews__next'),
          prevEl: el.querySelector('.flash-reviews__prev'),
        },
        breakpoints: {
          320: { spaceBetween: gapMobile, slidesPerView: slidesMobile },
          750: { spaceBetween: gap, slidesPerView: slidesTablet },
          990: { spaceBetween: gap, slidesPerView: slidesDesktop },
        },
      });
    });
  }

  /* ----------------------------------------------------------------- boot */
  function initAll(root) {
    root = root || document;
    root.querySelectorAll('[data-flash-countdown]').forEach(initCountdown);
    root.querySelectorAll('[data-flash-gift]').forEach(initGift);
    root.querySelectorAll('flash-ugc-videos').forEach(initUgc);
    root.querySelectorAll('flash-reviews').forEach(initReviews);
  }

  onReady(function () {
    initAll(document);
  });

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });
    document.addEventListener('shopify:block:select', function (e) {
      // keep countdown/gift working after block re-render
      initAll(e.target.closest('.shopify-section') || document);
    });
  }
})();
